/**
 * MAMU · Clara v3 — comportamiento mínimo del cromo (cabecera y pie).
 *
 * <mamu-cab>  (sections/mamu-cabecera.liquid)
 *   - mega menú por grupo: abre con hover (CSS), con clic / Enter / Espacio en el botón "+", se cierra con Esc,
 *     al salir el foco o al hacer clic fuera; flechas izquierda / derecha entre grupos;
 *   - barra de anuncios rotativa con flechas (pausa con hover / foco; sin autoplay si hay movimiento reducido);
 *   - hoja de menú móvil (<dialog>): botones [data-mamu-sheet-open], arrastre del asa y cierre por fondo / Esc;
 *   - hoja de búsqueda propia (<dialog class="mamu-bus">, botón "Buscar" de la tabbar): resultados predictivos con
 *     /search/suggest.json (debounce 200 ms, AbortController), chips, combobox/listbox accesible, Esc cierra;
 *   - cabecera pegada: borde en móvil con el scroll y variables --header-height / --header-group-height
 *     (las que usan otras partes de Horizon para colocarse bajo la cabecera);
 *   - el enlace de carrito de la tabbar abre el cajón de Horizon (#cart-drawer) si existe.
 *
 * <mamu-pie>  (sections/mamu-pie.liquid)
 *   - columnas del pie: acordeón exclusivo en móvil; siempre abiertas en escritorio.
 *
 * Sin dependencias. Todo funciona sin JS en lo básico (enlaces, formularios, <details>).
 */

const DESKTOP = '(min-width: 990px)';

class MamuCab extends HTMLElement {
  /** @type {AbortController | null} */
  #ac = null;
  /** @type {ResizeObserver | null} */
  #ro = null;
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {HTMLElement | null} */
  #sentinel = null;
  /** @type {number} */
  #timer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#initAlturas();
    this.#initScroll();
    this.#initAnuncio();
    this.#initMega();
    this.#initSheet();
    this.#initBuscar();
    this.#initCarrito();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
    this.#ro?.disconnect();
    this.#io?.disconnect();
    this.#sentinel?.remove();
    clearInterval(this.#timer);
  }

  get #signal() {
    return /** @type {AbortController} */ (this.#ac).signal;
  }

  /* ------------------------------------------------------------------ alturas / scroll */

  #initAlturas() {
    const section = this.closest('.shopify-section') ?? this;
    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-mamu-bar]'));
    if (!bar || !('ResizeObserver' in window)) return;

    const medir = () => {
      document.body.style.setProperty('--header-height', `${Math.round(bar.offsetHeight)}px`);
      document.body.style.setProperty('--header-group-height', `${Math.round(section.getBoundingClientRect().height)}px`);
    };

    medir();
    this.#ro = new ResizeObserver(medir);
    this.#ro.observe(bar);
    this.#ro.observe(section);
  }

  #initScroll() {
    const grupo = document.querySelector('#header-group');
    if (!grupo || !('IntersectionObserver' in window)) return;

    // Centinela de 1 px justo encima de la cabecera: cuando sale de pantalla, la página ya hizo scroll.
    this.#sentinel = document.createElement('div');
    this.#sentinel.setAttribute('aria-hidden', 'true');
    this.#sentinel.style.cssText = 'flex:none;height:1px;margin-bottom:-1px;pointer-events:none';
    grupo.before(this.#sentinel);

    this.#io = new IntersectionObserver(([entry]) => {
      this.classList.toggle('is-scrolled', !entry.isIntersecting);
    });
    this.#io.observe(this.#sentinel);
  }

  /* ------------------------------------------------------------------ anuncios */

  #initAnuncio() {
    const raiz = /** @type {HTMLElement | null} */ (this.querySelector('[data-mamu-anuncio]'));
    if (!raiz) return;

    const items = /** @type {HTMLElement[]} */ ([...raiz.querySelectorAll('.mamu-anuncio__item')]);
    if (items.length < 2) return;

    const lista = raiz.querySelector('[data-mamu-anuncio-lista]');
    const reducido = matchMedia('(prefers-reduced-motion: reduce)');
    const retardo = Number(raiz.dataset.delay) || 5000;
    let actual = 0;

    const mostrar = (/** @type {number} */ n) => {
      items[actual].hidden = true;
      items[actual].classList.remove('is-on');
      actual = (n + items.length) % items.length;
      items[actual].hidden = false;
      items[actual].classList.add('is-on');
    };

    const parar = () => {
      clearInterval(this.#timer);
      lista?.setAttribute('aria-live', 'polite');
    };

    const iniciar = () => {
      clearInterval(this.#timer);
      if (reducido.matches || document.hidden) return;
      lista?.setAttribute('aria-live', 'off');
      this.#timer = window.setInterval(() => mostrar(actual + 1), retardo);
    };

    raiz.querySelector('[data-mamu-anuncio-prev]')?.addEventListener('click', () => { mostrar(actual - 1); iniciar(); }, { signal: this.#signal });
    raiz.querySelector('[data-mamu-anuncio-next]')?.addEventListener('click', () => { mostrar(actual + 1); iniciar(); }, { signal: this.#signal });

    for (const ev of ['pointerenter', 'focusin']) raiz.addEventListener(ev, parar, { signal: this.#signal });
    for (const ev of ['pointerleave', 'focusout']) raiz.addEventListener(ev, iniciar, { signal: this.#signal });
    document.addEventListener('visibilitychange', iniciar, { signal: this.#signal });
    reducido.addEventListener('change', iniciar, { signal: this.#signal });

    iniciar();
  }

  /* ------------------------------------------------------------------ mega menú */

  #initMega() {
    const grupos = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-mamu-grupo]')]);
    if (!grupos.length) return;

    const fijar = (/** @type {HTMLElement} */ g, /** @type {boolean} */ abierto) => {
      const boton = g.querySelector('[data-mamu-mega-btn]');
      if (!boton) return;
      boton.setAttribute('aria-expanded', String(abierto));
      g.toggleAttribute('data-open', abierto);
    };
    const cerrarTodos = (/** @type {HTMLElement | null} */ salvo = null) => {
      for (const g of grupos) if (g !== salvo) fijar(g, false);
    };

    for (const g of grupos) {
      const boton = g.querySelector('[data-mamu-mega-btn]');
      if (!boton) continue;

      boton.addEventListener('click', () => {
        const abrir = boton.getAttribute('aria-expanded') !== 'true';
        cerrarTodos(g);
        g.removeAttribute('data-closed');
        fijar(g, abrir);
      }, { signal: this.#signal });

      g.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        cerrarTodos(g);
      }, { signal: this.#signal });

      g.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        g.removeAttribute('data-closed');
        fijar(g, false);
      }, { signal: this.#signal });

      g.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const visible = g.hasAttribute('data-open') || g.matches(':hover');
        if (!visible) return;
        e.stopPropagation();
        fijar(g, false);
        g.setAttribute('data-closed', ''); // apaga también el hover hasta que el puntero salga
        /** @type {HTMLElement} */ (boton).focus();
      }, { signal: this.#signal });

      g.addEventListener('focusout', (e) => {
        if (!(e.relatedTarget instanceof Node) || g.contains(e.relatedTarget)) return;
        fijar(g, false);
      }, { signal: this.#signal });
    }

    // Flechas izquierda / derecha entre los enlaces de grupo
    const nav = this.querySelector('[data-mamu-grupos]');
    nav?.addEventListener('keydown', (e) => {
      const ke = /** @type {KeyboardEvent} */ (e);
      if (ke.key !== 'ArrowLeft' && ke.key !== 'ArrowRight') return;
      const enlaces = /** @type {HTMLElement[]} */ ([...nav.querySelectorAll('.mamu-grupo__a')]);
      const g = /** @type {HTMLElement | null} */ ((ke.target instanceof Element) ? ke.target.closest('[data-mamu-grupo]') : null);
      const i = enlaces.findIndex((a) => g?.contains(a));
      if (i < 0) return;
      const siguiente = enlaces[(i + (ke.key === 'ArrowRight' ? 1 : -1) + enlaces.length) % enlaces.length];
      ke.preventDefault();
      siguiente.focus();
    }, { signal: this.#signal });

    document.addEventListener('pointerdown', (e) => {
      if (e.target instanceof Node && !this.contains(e.target)) cerrarTodos();
    }, { signal: this.#signal });
  }

  /* ------------------------------------------------------------------ hoja de menú móvil */

  #initSheet() {
    const hoja = /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog[data-mamu-sheet]'));
    if (!hoja) return;

    const aperturas = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-mamu-sheet-open]')]);
    const conEstado = aperturas.filter((b) => b.hasAttribute('aria-expanded'));
    const marcar = (/** @type {boolean} */ v) => conEstado.forEach((b) => b.setAttribute('aria-expanded', String(v)));

    const abrir = (/** @type {boolean} */ buscar) => {
      if (!hoja.open) hoja.showModal();
      marcar(true);
      if (buscar) /** @type {HTMLElement | null} */ (hoja.querySelector('input[type="search"]'))?.focus();
    };
    const cerrar = () => {
      hoja.classList.remove('is-drag');
      hoja.style.translate = '';
      if (hoja.open) hoja.close();
    };

    for (const b of aperturas) {
      b.addEventListener('click', (e) => { e.preventDefault(); abrir(b.dataset.mamuSheetOpen === 'buscar'); }, { signal: this.#signal });
    }
    for (const b of hoja.querySelectorAll('[data-mamu-sheet-close]')) {
      b.addEventListener('click', cerrar, { signal: this.#signal });
    }
    hoja.addEventListener('close', () => marcar(false), { signal: this.#signal });
    hoja.addEventListener('click', (e) => { if (e.target === hoja) cerrar(); }, { signal: this.#signal });
    matchMedia(DESKTOP).addEventListener('change', (e) => { if (e.matches) cerrar(); }, { signal: this.#signal });

    // Arrastrar el asa hacia abajo para cerrar
    const asa = hoja.querySelector('[data-mamu-grab]');
    if (asa) {
      let y0 = 0;
      let dy = 0;
      let arrastrando = false;
      asa.addEventListener('pointerdown', (e) => {
        const pe = /** @type {PointerEvent} */ (e);
        arrastrando = true;
        y0 = pe.clientY;
        dy = 0;
        hoja.classList.add('is-drag');
        asa.setPointerCapture(pe.pointerId);
      }, { signal: this.#signal });
      asa.addEventListener('pointermove', (e) => {
        if (!arrastrando) return;
        dy = Math.max(0, /** @type {PointerEvent} */ (e).clientY - y0);
        hoja.style.translate = `0 ${dy}px`;
      }, { signal: this.#signal });
      const fin = () => {
        if (!arrastrando) return;
        arrastrando = false;
        hoja.classList.remove('is-drag');
        if (dy > 110) cerrar();
        else hoja.style.translate = '';
      };
      asa.addEventListener('pointerup', fin, { signal: this.#signal });
      asa.addEventListener('pointercancel', fin, { signal: this.#signal });
    }

    if (location.hash === '#menu' && !hoja.open) abrir(false);
  }

  /* ------------------------------------------------------------------ hoja de búsqueda móvil */

  /**
   * Hoja de búsqueda propia (<dialog class="mamu-bus">): resultados en vivo con la Predictive Search API de Shopify
   * (/search/suggest.json), debounce 200 ms, cancelación con AbortController, patrón combobox/listbox y estado vacío.
   * Antes de escribir se ve el bloque estático (chips, promociones, nueva temporada) que pinta el Liquid.
   */
  #initBuscar() {
    const hoja = /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog[data-mamu-bus]'));
    if (!hoja) return;

    const signal = this.#signal;
    const menu = /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog[data-mamu-sheet]'));
    const input = /** @type {HTMLInputElement} */ (hoja.querySelector('[data-mamu-bus-input]'));
    const form = /** @type {HTMLFormElement} */ (hoja.querySelector('[data-mamu-bus-form]'));
    const borrar = /** @type {HTMLElement} */ (hoja.querySelector('[data-mamu-bus-clear]'));
    const idle = /** @type {HTMLElement} */ (hoja.querySelector('[data-mamu-bus-idle]'));
    const res = /** @type {HTMLElement} */ (hoja.querySelector('[data-mamu-bus-res]'));
    const lista = /** @type {HTMLElement} */ (hoja.querySelector('[data-mamu-bus-list]'));
    const estado = /** @type {HTMLElement} */ (hoja.querySelector('[data-mamu-bus-status]'));
    const cuerpo = /** @type {HTMLElement} */ (hoja.querySelector('[data-mamu-bus-body]'));
    const chips = hoja.querySelector('[data-mamu-bus-chips]');
    if (!input || !form || !borrar || !idle || !res || !lista || !estado) return;

    const d = hoja.dataset;
    const url = d.url || '/search/suggest';
    const urlBusqueda = d.searchUrl || '/search';
    const moneda = d.currency || 'MXN';
    let idioma = (d.locale || document.documentElement.lang || 'es-MX').replace('_', '-');
    if (idioma === 'es') idioma = 'es-MX'; // peso mexicano con coma de millares: $26,999
    const tt = (/** @type {string | undefined} */ s, /** @type {Record<string, string | number>} */ v = {}) =>
      (s || '').replace(/%(\w+)%/g, (m, k) => (k in v ? String(v[k]) : m));

    /** @type {Map<string, any>} */
    const cache = new Map();
    /** @type {AbortController | null} */
    let ctl = null;
    let temporizador = 0;
    let activo = -1;
    const MIN = 2;

    const dinero = (/** @type {number} */ n) => {
      try {
        return new Intl.NumberFormat(idioma, {
          style: 'currency',
          currency: moneda,
          minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
          maximumFractionDigits: 2,
        }).format(n);
      } catch {
        return `$${n}`;
      }
    };

    const el = (/** @type {string} */ tag, /** @type {string} */ clase = '', /** @type {string} */ texto = '') => {
      const e = document.createElement(tag);
      if (clase) e.className = clase;
      if (texto) e.textContent = texto;
      return e;
    };

    const opciones = () => /** @type {HTMLElement[]} */ ([...lista.querySelectorAll('[role="option"]')]);

    const marcar = (/** @type {number} */ i) => {
      const ops = opciones();
      ops.forEach((o, n) => {
        const on = n === i;
        o.classList.toggle('is-activa', on);
        o.setAttribute('aria-selected', String(on));
      });
      activo = i;
      if (i >= 0 && ops[i]) {
        input.setAttribute('aria-activedescendant', ops[i].id);
        ops[i].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    };

    const mostrarResultados = (/** @type {boolean} */ v) => {
      res.hidden = !v;
      idle.hidden = v;
      input.setAttribute('aria-expanded', String(v && opciones().length > 0));
      if (!v) {
        lista.replaceChildren();
        estado.textContent = '';
        marcar(-1);
      }
    };

    let seq = 0;
    const opcion = (/** @type {HTMLAnchorElement} */ a) => {
      a.setAttribute('role', 'option');
      a.setAttribute('aria-selected', 'false');
      a.id = `${hoja.id}-o${seq++}`;
      return a;
    };

    const grupo = (/** @type {string} */ titulo) => {
      const g = el('div', 'mamu-bus__grupo');
      g.setAttribute('role', 'group');
      const h = el('p', 'mamu-bus__h', titulo);
      const id = `${hoja.id}-g${seq++}`;
      h.id = id;
      g.setAttribute('aria-labelledby', id);
      g.append(h);
      return g;
    };

    const miniatura = (/** @type {any} */ p) => {
      const src = p.featured_image?.url || p.image;
      const caja = el('span', 'mamu-bus-res__img');
      if (src) {
        try {
          const u = new URL(src, location.href);
          u.searchParams.set('width', '160');
          const img = document.createElement('img');
          img.src = u.toString();
          img.alt = '';
          img.width = 80;
          img.height = 80;
          img.loading = 'lazy';
          caja.append(img);
        } catch {
          /* sin foto */
        }
      }
      return caja;
    };

    const tarjetaProducto = (/** @type {any} */ p) => {
      const a = opcion(/** @type {HTMLAnchorElement} */ (el('a', 'mamu-bus-res')));
      a.href = p.url;
      a.append(miniatura(p));
      const txt = el('span', 'mamu-bus-res__txt');
      txt.append(el('span', 'mamu-bus-res__t', p.title));
      const precio = Number.parseFloat(p.price);
      const antes = Number.parseFloat(p.compare_at_price);
      const fila = el('span', 'mamu-bus-res__p mamu-tnum');
      if (!(precio > 0)) {
        fila.append(el('b', '', d.tQuote || ''));
      } else {
        fila.append(el('b', '', dinero(precio)));
        if (antes > precio) {
          const s = el('s', '', dinero(antes));
          s.setAttribute('aria-label', `${d.tWas || ''} ${dinero(antes)}`);
          fila.append(s);
        }
      }
      txt.append(fila);
      if (precio > 0 && antes > precio) {
        txt.append(el('span', 'mamu-bus-res__ahorro mamu-tnum', tt(d.tSave, { amount: dinero(antes - precio) })));
      }
      a.append(txt);
      return a;
    };

    const iconoBuscar = () => {
      const tpl = hoja.querySelector('.mamu-bus__campo svg');
      return tpl ? /** @type {SVGElement} */ (tpl.cloneNode(true)) : null;
    };

    const pintar = (/** @type {string} */ q, /** @type {any} */ datos) => {
      seq = 0;
      lista.replaceChildren();
      const r = datos?.resources?.results || {};
      const productos = r.products || [];
      const colecciones = r.collections || [];
      const consultas = r.queries || [];
      const total = productos.length + colecciones.length + consultas.length;

      if (!total) {
        const vacio = el('div', 'mamu-bus__vacio');
        vacio.append(el('p', 'mamu-bus__vacio-t', tt(d.tNone, { q })));
        vacio.append(el('p', 'mamu-bus__vacio-p', d.tNoneHelp || ''));
        if (chips) {
          const copia = /** @type {HTMLElement} */ (chips.cloneNode(true));
          copia.removeAttribute('data-mamu-bus-chips');
          vacio.append(copia);
        }
        lista.append(vacio);
        estado.textContent = tt(d.tNone, { q });
        input.setAttribute('aria-expanded', 'false');
        return;
      }

      if (consultas.length) {
        const g = grupo(d.tQueries || '');
        for (const c of consultas) {
          const a = opcion(/** @type {HTMLAnchorElement} */ (el('a', 'mamu-bus-fila')));
          a.href = c.url || `${urlBusqueda}?q=${encodeURIComponent(c.text)}`;
          a.dataset.q = c.text;
          const ic = iconoBuscar();
          if (ic) a.append(ic);
          a.append(el('span', '', c.text));
          g.append(a);
        }
        lista.append(g);
      }
      if (colecciones.length) {
        const g = grupo(d.tCollections || '');
        for (const c of colecciones) {
          const a = opcion(/** @type {HTMLAnchorElement} */ (el('a', 'mamu-bus-fila mamu-bus-fila--col')));
          a.href = c.url;
          a.append(el('span', '', c.title));
          g.append(a);
        }
        lista.append(g);
      }
      if (productos.length) {
        const g = grupo(d.tProducts || '');
        for (const p of productos) g.append(tarjetaProducto(p));
        lista.append(g);
      }

      const todos = opcion(/** @type {HTMLAnchorElement} */ (el('a', 'mamu-bus__todos')));
      todos.href = `${urlBusqueda}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
      todos.textContent = tt(d.tViewAll, { q });
      lista.append(todos);

      input.setAttribute('aria-expanded', 'true');
      estado.textContent = tt(d.tFound, { total, q });
    };

    const pintarError = (/** @type {string} */ q) => {
      lista.replaceChildren();
      const v = el('div', 'mamu-bus__vacio');
      v.append(el('p', 'mamu-bus__vacio-p', d.tError || ''));
      const a = el('a', 'mamu-bus__todos');
      a.setAttribute('href', `${urlBusqueda}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`);
      a.textContent = tt(d.tViewAll, { q });
      v.append(a);
      lista.append(v);
      estado.textContent = d.tError || '';
      input.setAttribute('aria-expanded', 'false');
    };

    const buscar = async (/** @type {string} */ q) => {
      ctl?.abort();
      const clave = q.toLowerCase();
      mostrarResultados(true);
      if (cache.has(clave)) {
        pintar(q, cache.get(clave));
        marcar(-1);
        return;
      }
      ctl = new AbortController();
      res.setAttribute('aria-busy', 'true');
      estado.textContent = d.tLoading || '';
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,tag,variants.title',
      });
      try {
        const r = await fetch(`${url}.json?${params}`, { signal: ctl.signal, headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error(String(r.status));
        const datos = await r.json();
        cache.set(clave, datos);
        pintar(q, datos);
        marcar(-1);
      } catch (e) {
        if (/** @type {Error} */ (e).name === 'AbortError') return;
        pintarError(q);
      } finally {
        res.removeAttribute('aria-busy');
      }
    };

    const alEscribir = () => {
      const q = input.value.trim();
      borrar.hidden = input.value.length === 0;
      clearTimeout(temporizador);
      if (q.length < MIN) {
        ctl?.abort();
        mostrarResultados(false);
        return;
      }
      temporizador = window.setTimeout(() => buscar(q), 200);
    };

    const abrir = () => {
      if (menu?.open) {
        menu.classList.remove('is-drag');
        menu.style.translate = '';
        menu.close();
      }
      if (!hoja.open) hoja.showModal();
      for (const b of this.querySelectorAll('[data-mamu-bus-open][aria-expanded]')) b.setAttribute('aria-expanded', 'true');
      input.focus({ preventScroll: true });
    };
    const cerrar = () => {
      if (hoja.open) hoja.close();
    };

    for (const b of this.querySelectorAll('[data-mamu-bus-open]')) {
      b.addEventListener('click', (e) => { e.preventDefault(); abrir(); }, { signal });
    }
    hoja.querySelector('[data-mamu-bus-close]')?.addEventListener('click', cerrar, { signal });
    hoja.addEventListener('close', () => {
      for (const b of this.querySelectorAll('[data-mamu-bus-open][aria-expanded]')) b.setAttribute('aria-expanded', 'false');
      clearTimeout(temporizador);
      ctl?.abort();
      input.value = '';
      borrar.hidden = true;
      mostrarResultados(false);
      cuerpo.scrollTop = 0;
    }, { signal });
    matchMedia(DESKTOP).addEventListener('change', (e) => { if (e.matches) cerrar(); }, { signal });

    input.addEventListener('input', alEscribir, { signal });
    borrar.addEventListener('click', () => {
      input.value = '';
      alEscribir();
      input.focus();
    }, { signal });

    // Chips y sugerencias de consulta: rellenan el campo y buscan en el acto (sin recargar la página)
    hoja.addEventListener('click', (e) => {
      const a = e.target instanceof Element ? e.target.closest('a[data-q]') : null;
      if (!a) return;
      const me = /** @type {MouseEvent} */ (e);
      if (me.metaKey || me.ctrlKey || me.shiftKey || me.button !== 0) return;
      e.preventDefault();
      input.value = /** @type {HTMLElement} */ (a).dataset.q || '';
      borrar.hidden = input.value.length === 0;
      clearTimeout(temporizador);
      if (input.value.trim().length >= MIN) buscar(input.value.trim());
      input.focus();
    }, { signal });

    // Teclado: flechas recorren los resultados, Enter abre el activo o envía la búsqueda completa
    input.addEventListener('keydown', (e) => {
      const ops = opciones();
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!ops.length || res.hidden) return;
        e.preventDefault();
        const paso = e.key === 'ArrowDown' ? 1 : -1;
        marcar(activo < 0 ? (paso > 0 ? 0 : ops.length - 1) : (activo + paso + ops.length) % ops.length);
      } else if (e.key === 'Enter' && activo >= 0 && ops[activo]) {
        e.preventDefault();
        ops[activo].click();
      } else if (e.key === 'Escape') {
        // En un campo de búsqueda el primer Esc solo borra el texto; aquí Esc siempre cierra la hoja
        e.preventDefault();
        cerrar();
      }
    }, { signal });

    form.addEventListener('submit', (e) => {
      if (!input.value.trim()) e.preventDefault();
    }, { signal });

    if (location.hash === '#buscar' && !hoja.open) abrir();
  }

  /* ------------------------------------------------------------------ carrito en la tabbar */

  #initCarrito() {
    for (const a of this.querySelectorAll('[data-mamu-cart-link]')) {
      a.addEventListener('click', (e) => {
        const me = /** @type {MouseEvent} */ (e);
        if (me.metaKey || me.ctrlKey || me.shiftKey || me.button !== 0) return;
        const cajon = /** @type {HTMLElement & { toggle?: () => void } | null} */ (document.getElementById('cart-drawer'));
        if (cajon && typeof cajon.toggle === 'function') {
          e.preventDefault();
          cajon.toggle();
        }
      }, { signal: this.#signal });
    }
  }
}

class MamuPie extends HTMLElement {
  /** @type {AbortController | null} */
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const mq = matchMedia(DESKTOP);
    const accs = /** @type {HTMLDetailsElement[]} */ ([...this.querySelectorAll('details.mamu-acc')]);
    if (!accs.length) return;

    // Escritorio: siempre abiertas y su resumen no es interactivo. Móvil: solo la primera abierta.
    const aplicar = () => {
      accs.forEach((d, i) => {
        d.open = mq.matches || i === 0;
        const resumen = d.querySelector('summary');
        if (mq.matches) resumen?.setAttribute('tabindex', '-1');
        else resumen?.removeAttribute('tabindex');
      });
    };

    for (const d of accs) {
      d.addEventListener('toggle', () => {
        if (mq.matches) {
          if (!d.open) d.open = true;
          return;
        }
        if (d.open) for (const otra of accs) if (otra !== d) otra.open = false;
      }, { signal });
    }

    mq.addEventListener('change', aplicar, { signal });
    aplicar();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }
}

if (!customElements.get('mamu-cab')) customElements.define('mamu-cab', MamuCab);
if (!customElements.get('mamu-pie')) customElements.define('mamu-pie', MamuPie);
